/**
 * THE TIME MACHINE, for staff. The What Matters microsite's `wc-mode` /
 * `wc-viewer` cookies, generalised and moved to the URL so a link can be
 * shared with the team:
 *
 *   ?stage=recap              the page on a representative day of that stage
 *   ?at=2026-11-07T10:52      the page at an exact moment (wall clock)
 *   ?as=guest | attendee      signed out, or a made-up registrant (kit/demo)
 *   ?xp=what-matters-2027     wear a different experience on this event
 *
 * Honoured only for staff (the widget token's isStaff) or on a dev host;
 * anyone else gets the real page whatever the URL says. While pretending,
 * writes are refused: a recap preview must not change a real pick.
 */
import { useEffect, useState } from 'react';
import { STAGES, type Stage } from '@/lib/stages';
import type { Viewer } from '../types';

export interface Preview {
  allowed: boolean;
  stage: Stage | null;
  at: string | null;
  viewer: Viewer;
  xp: string | null;
}

const isDevHost = () => /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

function read(): Omit<Preview, 'allowed'> {
  const q = new URLSearchParams(window.location.search);
  const stage = q.get('stage');
  const at = q.get('at');
  const as = q.get('as');
  const xp = q.get('xp');
  return {
    stage: stage && (STAGES as string[]).includes(stage) ? (stage as Stage) : null,
    at:
      at && /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/.test(at)
        ? at.length === 10
          ? `${at}T12:00:00`
          : at.length === 16
            ? `${at}:00`
            : at
        : null,
    viewer: as === 'guest' || as === 'attendee' ? as : 'real',
    xp: xp && /^[a-z][a-z0-9-]{1,59}$/.test(xp) ? xp : null,
  };
}

export function usePreview(
  isStaff: boolean
): [Preview, (next: Partial<Omit<Preview, 'allowed'>>) => void] {
  const allowed = isStaff || isDevHost();
  const [state, setState] = useState(read);
  useEffect(() => {
    const onPop = () => setState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const set = (next: Partial<Omit<Preview, 'allowed'>>) => {
    const merged = { ...state, ...next };
    const q = new URLSearchParams(window.location.search);
    const put = (k: string, v: string | null) => (v ? q.set(k, v) : q.delete(k));
    put('stage', merged.stage);
    put('at', merged.at);
    put('as', merged.viewer === 'real' ? null : merged.viewer);
    put('xp', merged.xp);
    const qs = q.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
    );
    setState(merged);
  };
  if (!allowed) return [{ allowed, stage: null, at: null, viewer: 'real', xp: null }, () => {}];
  return [{ allowed, ...state }, set];
}

/** Whether the time machine is changing anything right now. */
export const pretending = (p: Preview) =>
  p.allowed && (p.stage !== null || p.at !== null || p.viewer !== 'real' || p.xp !== null);
